import { Component, OnInit, Input } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES, AxwayTokenFetcherService } from '@ptl/xhr';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { FindMemberRequestPayload, MEMBER_ID_TYPE } from '../../interfaces/find-member-request.interface';
import { FindMemberResponse } from '../../interfaces/find-member-response.interface';
import { NormalizedResponse } from 'src/app/shared/interfaces/http-requests/internal/response.interface';
import { UserInfoStateStructure, LOGIN_INFO_REDUCER_NAME, LoginInfo, EntitlementsHelperService, LINE_OF_BUSINESS_ENUM, ENTITLEMENTS, USER_ROLE } from '@ptl/account-management';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthoringModeService } from 'src/app/shared/services/authoring-mode.service';
import { environment } from 'src/environments/environment';
import { saveMemberID } from '../configs/reducers/globalData.reducer';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { API_ENDPOINTS } from 'src/app/shared/constants/api-endpoints.constant';
import { BrandingConfigurationService } from '@ptl/brand';

@Component({
    selector: 'app-find-customer',
    templateUrl: './find-customer.component.html',
    styleUrls: ['./find-customer.component.scss'],
    standalone: false
})
export class FindCustomerComponent implements OnInit {

  @Input() healthSherpaRegionConfig: string;
  @Input() enrollPageUrl: string;
  @Input() buttonContentSubmitAsHealthSherpa: string;
  @Input() buttonContentSubmitAsShopX: string;

  searchForm: UntypedFormGroup;

  hideMemberIDOrUMI = true;
  hideAgreementNumber = true;
  hideSSN = true;

  hasEntitlements = false;

  memberResponse: NormalizedResponse<FindMemberResponse>;

  constructor(
    private fb: UntypedFormBuilder,
    private entitlementsHelper: EntitlementsHelperService,
    private reducerUtilityService: ReducerUtilityService,
    private httpClient: HTTPWrapperContext,
    private authoringMode: AuthoringModeService,
    private entitlementsHelperService: EntitlementsHelperService,
    private matSnackBar: MatSnackBar,
    private ngRedux: NgRedux<StateStructure>,
    private axwayTokenFetcherService: AxwayTokenFetcherService,
    private brandingConfigurationService: BrandingConfigurationService
  ) {

    this.searchForm = this.fb.group({
      searchType: this.fb.control('', [Validators.required]),
      memberIDOrUMI: this.fb.control('', [Validators.required, Validators.maxLength(12), Validators.pattern('^[0-9]+$')]),
      agreementNumber: this.fb.control('', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]),
      ssn: this.fb.control('', [Validators.required, Validators.minLength(9), Validators.maxLength(9), Validators.pattern('^[0-9]*$')])
    });

  }

  async ngOnInit() {

    this.hasEntitlements = this.authoringMode.getAuthoringMode();

    // TODO: 3/24/2025 - this seems to be more hardcoded than it needs to be, can we fix this? 
    if (['MN', 'HM'].includes(environment.initData.brand.ASPID)) {

      this.hasEntitlements = true;

    } else {

      const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

      const lob: LINE_OF_BUSINESS_ENUM = await this.entitlementsHelper.getCurrentLobPromise();
      const loginData: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

      const isGeneralAgencyAdmin = await this.entitlementsHelper.entitlementValueForSelectedAgencyByNameAndLob(ENTITLEMENTS.GENERAL_AGENCY_ADMIN, lob, loginData?.userInfo?.role as USER_ROLE);

      this.hasEntitlements = !!loginInfo.userInfo?.ffmId || isGeneralAgencyAdmin;
    }

  }

  async submit(form: UntypedFormGroup) {

    const selectedAgency = await this.entitlementsHelperService.getSelectedAgencyPromise();
    const selectedAgencyId = selectedAgency.agencyId;
    const selectedAgencyIdPadded = (selectedAgencyId || '').padStart(10, '0');

    let queryType = null;
    let queryValue = null;

    const searchType = form.value['searchType'];
    let searchErrorType;

    switch (searchType) {

      case 'memberIDOrUMI':
        queryType = MEMBER_ID_TYPE.UMI;
        queryValue = form.value['memberIDOrUMI'];
        searchErrorType = 'Member ID or UMI';
        break;
      case 'agreementNumber':
        queryType = MEMBER_ID_TYPE.AGR;
        queryValue = form.value['agreementNumber'];
        searchErrorType = 'Agreement Number';
        break;
      case 'ssn':
        queryType = MEMBER_ID_TYPE.SSN;
        queryValue = form.value['ssn'];
        searchErrorType = 'Social Security Number';
        break;
    }

    /** Service doesn't return meaingful errors, just 404 and 500s - try catch handle this */
    try {

      const response = await this.getMemberDetails(queryType, queryValue, 
        (environment.initData.brand.ASPID !== 'HM') ? selectedAgencyIdPadded : selectedAgencyId);

      if (!(response instanceof HttpErrorResponse)) {
        this.memberResponse = response;
        this.ngRedux.dispatch(saveMemberID(queryValue))
      } else {
        this.matSnackBar.open(`Service Failed`, 'OK', {
          duration: 5000,
          panelClass: 'negative-panel-class'
        });
      }

    } catch (e) {
      const errorResponse = e as HttpErrorResponse;

      switch (errorResponse.status) {
        case 404: this.matSnackBar.open(`No results found for ${searchErrorType} entered.`, 'OK', {
            duration: 5000,
            panelClass: 'negative-panel-class'
          });
          break;
        case 500: this.matSnackBar.open(`${searchErrorType} format invalid for service.`, 'OK', {
            duration: 5000,
            panelClass: 'negative-panel-class'
          });
          break;
        default: this.matSnackBar.open(`Service Failed`, 'OK', {
            duration: 5000,
            panelClass: 'positive-panel-class'
          });
          break;
      }
    }

  }

  getMemberDetails(
    queryType: MEMBER_ID_TYPE,
    queryValue: string,
    paddedSelectedAgencyId: string
  ): Promise<NormalizedResponse<FindMemberResponse>> {

    const payload: FindMemberRequestPayload = {
      payload: {
        idType: queryType,
        memberId: queryValue,
        agencyId: paddedSelectedAgencyId
      }
    };

    // console.log(payload);

    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        FromCSR: loginInfo.userId,
        ddToken: this.brandingConfigurationService.getCurrentBrand().ddToken_encoded()
      }),
      POST_OR_PUT_PAYLOAD: payload,
      url: this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.ENROLLMENT.INDIVIDUAL_RENEWAL, {})
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    return this.httpClient.makeRequest<NormalizedResponse<FindMemberResponse>>(requestConfig);
  }

  searchTypeChange(searchType: any) {
    if (searchType === 'memberIDOrUMI') {
      this.hideMemberIDOrUMI = false;
      this.hideAgreementNumber = true;
      this.hideSSN = true;
      this.searchForm.controls['agreementNumber'].setValue('');
      this.searchForm.controls['ssn'].setValue('');
    } else if (searchType === 'agreementNumber') {
      this.hideMemberIDOrUMI = true;
      this.hideAgreementNumber = false;
      this.hideSSN = true;
      this.searchForm.controls['memberIDOrUMI'].setValue('');
      this.searchForm.controls['ssn'].setValue('');
    } else if (searchType === 'ssn') {
      this.hideMemberIDOrUMI = true;
      this.hideAgreementNumber = true;
      this.hideSSN = false;
      this.searchForm.controls['memberIDOrUMI'].setValue('');
      this.searchForm.controls['agreementNumber'].setValue('');
    }
  }
}
